import React, { useEffect, useMemo } from 'react';
import { PersonSummaryWithPhoto } from '../types';
import PersonPhoto from './PersonPhoto';
import { hasPersonDetailPage } from '../utils/navigation';
import { usePersonPhotoBatchLoader, usePersonPhotoLoadingState } from '../hooks/usePersonPhotoPerformance';
import { getConfig } from '../config';

interface PeoplePhotosOverlayProps {
  people: PersonSummaryWithPhoto[];
  maxVisible?: number;
  onPersonClick?: (person: PersonSummaryWithPhoto) => void;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  enableOptimizations?: boolean;
  context?: 'overlay' | 'grid' | 'detail' | 'thumbnail';
}

const PeoplePhotosOverlay: React.FC<PeoplePhotosOverlayProps> = ({
  people,
  maxVisible = 4,
  onPersonClick,
  className = '',
  size = 'small',
  enableOptimizations = true,
  context = 'overlay'
}) => {
  const config = getConfig();
  
  // Safely use performance hooks only when optimizations are enabled
  let preloadPersonPhotos: any = () => {};
  let loadingState: any = { isLoading: false, loadedCount: 0, totalCount: 0, progress: 100 };
  
  try {
    if (enableOptimizations) {
      const batchLoader = usePersonPhotoBatchLoader();
      const loadingStateHook = usePersonPhotoLoadingState(people);
      preloadPersonPhotos = batchLoader.preloadPersonPhotos;
      loadingState = loadingStateHook.loadingState;
    }
  } catch (error) {
    // Performance hooks not available, continue without optimizations
  }

  // Don't render if no people data
  if (!people || people.length === 0) {
    return null;
  }

  // Get visible people and calculate remaining count
  const visiblePeople = useMemo(() => people.slice(0, maxVisible), [people, maxVisible]);
  const remainingCount = Math.max(0, people.length - maxVisible);

  // Preload images for better performance
  useEffect(() => {
    if (enableOptimizations && config.enableLazyLoading && visiblePeople.length > 0) {
      preloadPersonPhotos(visiblePeople, context, size);
    }
  }, [visiblePeople, enableOptimizations, config.enableLazyLoading, preloadPersonPhotos, context, size]);

  // Size configurations for the overlay container
  const sizeConfig = {
    small: {
      container: 'gap-1',
      moreIndicator: 'w-10 h-10 text-xs',
      spacing: 'space-x-1'
    },
    medium: {
      container: 'gap-1.5',
      moreIndicator: 'w-12 h-12 text-sm',
      spacing: 'space-x-1.5'
    },
    large: {
      container: 'gap-2',
      moreIndicator: 'w-16 h-16 text-base',
      spacing: 'space-x-2'
    }
  };

  const styleConfig = sizeConfig[size];

  const handlePersonClick = (person: PersonSummaryWithPhoto) => {
    if (onPersonClick) {
      onPersonClick(person);
    }
  };

  return (
    <div className={`flex items-center ${styleConfig.container} ${className}`}>
      {/* Render visible people photos */}
      {visiblePeople.map((person, index) => {
        // Only make person clickable if they have a detail page and onPersonClick is provided
        const isClickable = onPersonClick && hasPersonDetailPage(person);
        
        return (
          <PersonPhoto
            key={`${person.id}-${index}`}
            person={person}
            size={size}
            onClick={isClickable ? () => handlePersonClick(person) : undefined}
            showTooltip={true}
            className="flex-shrink-0"
            context={context}
            enableOptimizations={enableOptimizations}
          />
        );
      })}

      {/* Show "+X more" indicator if there are additional people */}
      {remainingCount > 0 && (
        <div
          className={`
            ${styleConfig.moreIndicator}
            rounded-full
            bg-gray-700/80
            border-2
            border-gray-600
            flex
            items-center
            justify-center
            text-white
            font-semibold
            flex-shrink-0
            cursor-pointer
            hover:bg-gray-600/80
            transition-colors
            duration-200
          `}
          title={`${remainingCount} more ${remainingCount === 1 ? 'person' : 'people'}`}
          style={enableOptimizations ? { willChange: 'background-color' } : undefined}
        >
          +{remainingCount}
        </div>
      )}

      {/* Performance indicator in debug mode */}
      {config.enableDebugMode && enableOptimizations && (
        <div className="ml-2 text-xs text-gray-500">
          {loadingState.isLoading ? (
            <span>⏳ {loadingState.progress.toFixed(0)}%</span>
          ) : (
            <span>✓ {loadingState.loadedCount}/{loadingState.totalCount}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default PeoplePhotosOverlay;